import { useState, useRef, useCallback, useEffect } from "react";
import TokenService from "../services/TokenService";

const useAutodeskViewer = () => {
  const [accessToken, setAccessToken] = useState("");
  const [viewer, setViewer] = useState(null);
  const [status, setStatus] = useState({
    type: "info",
    message: "Initializing...",
  });
  const [loading, setLoading] = useState(false);
  const viewerRef = useRef(null);

  // Auto-initialize on mount
  const initializeApp = useCallback(async () => {
    setLoading(true);
    setStatus({ type: "info", message: "Checking environment variables..." });

    // Check if environment variables are set
    if (
      !process.env.REACT_APP_FORGE_CLIENT_ID ||
      !process.env.REACT_APP_FORGE_CLIENT_SECRET
    ) {
      setStatus({
        type: "error",
        message:
          "Forge credentials not found in .env file. Please check REACT_APP_FORGE_CLIENT_ID and REACT_APP_FORGE_CLIENT_SECRET",
      });
      setLoading(false);
      return;
    }

    setStatus({ type: "info", message: "Getting access token..." });

    try {
      // Check if we have a valid stored token first
      const storedToken = TokenService.getStoredToken();
      if (storedToken) {
        setAccessToken(storedToken);
        setStatus({
          type: "success",
          message: "Access token loaded from storage!",
        });
        setLoading(false);
        return;
      }

      // Get new token
      const tokenData = await TokenService.getAccessToken();

      TokenService.storeToken(tokenData);
      setAccessToken(tokenData.access_token);
      setStatus({
        type: "success",
        message: "Access token obtained successfully! Ready to load models.",
      });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!accessToken) return;

    const checkTokenExpiry = () => {
      const storedToken = TokenService.getStoredToken();
      if (!storedToken) {
        // Token expired, get new one
        initializeApp();
      }
    };

    // Check every 30 minutes
    const interval = setInterval(checkTokenExpiry, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, [accessToken, initializeApp]);

  const loadModel = useCallback(
    (documentUrn) => {
      if (!accessToken) {
        setStatus({ type: "error", message: "Access token not available" });
        return;
      }

      if (!documentUrn) {
        setStatus({
          type: "error",
          message: "Please provide document URN or upload a file",
        });
        return;
      }

      setStatus({ type: "info", message: "Loading model..." });
    },
    [accessToken]
  );

  const handleViewerReady = useCallback((viewerInstance) => {
    setViewer(viewerInstance);
    viewerRef.current = viewerInstance;
    setStatus({
      type: "success",
      message: "Viewer ready! Load a model to get started.",
    });
  }, []);

  const handleDocumentLoaded = useCallback((doc) => {
    setStatus({ type: "success", message: "Model loaded successfully!" });
  }, []);

  const handleError = useCallback((error) => {
    setStatus({ type: "error", message: error });
  }, []);

  const handleLoadingChange = useCallback((isLoading) => {
    setLoading(isLoading);
  }, []);

  // Viewer control functions
  const fitToWindow = useCallback(() => {
    if (viewerRef.current) {
      viewerRef.current.fitToView();
    }
  }, []);

  const resetView = useCallback(() => {
    if (viewerRef.current) {
      viewerRef.current.restoreDefaultSettings();
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (viewerRef.current) {
      if (viewerRef.current.isFullscreen()) {
        viewerRef.current.exitFullscreen();
      } else {
        viewerRef.current.enterFullscreen();
      }
    }
  }, []);

  return {
    accessToken,
    viewer,
    status,
    loading,
    initializeApp,
    loadModel,
    handleViewerReady,
    handleDocumentLoaded,
    handleError,
    handleLoadingChange,
    fitToWindow,
    resetView,
    toggleFullscreen,
  };
};

export default useAutodeskViewer;
